import React, { useEffect, useState } from 'react';
import {
	Button,
	VStack,
	Text,
	HStack,
} from '@montel/montelpro-shared-components-v2';
import { StandardModal } from 'components/common/Modal/StandardModal';
import {
	Subscription,
	SubscriptionStatus,
} from 'domain/subscription/interfaces/Subscription';
import { CompanySelect } from 'components/companies/CompanySelect';
import { useGetOnMount } from 'hooks/useGetOnMount';
import {
	ACCOUNT_API_COMPANIES,
	SUBSCRIPTIONS_API_ROUTE,
	SUBSCRIPTIONS_GET_BY_COMPANY,
} from 'apiRoutes';
import { Company } from 'domain/company/Company';
import { RequestStatus } from 'types/RequestStatus';
import useSubmit, { POST } from 'hooks/useSubmit';
import SubscriptionTable from 'components/subscription/SubscriptionTable';
import { subscriptionTableColumns } from 'components/subscription/subscriptionTableColumns';
import { useGet } from 'hooks/useGet';
import { Row } from '@tanstack/react-table';

interface ReallocateSubscriptionModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: (targetSubscriptionId: number) => void;
	originalSubscription?: Subscription;
}

export const ReallocateSubscriptionModal: React.FC<
	ReallocateSubscriptionModalProps
> = ({ isOpen, onClose, onSuccess, originalSubscription }) => {
	const [selectedSubscriptionId, setSelectedSubscriptionId] =
		useState<number>();
	const [selectedCompany, setSelectedCompany] = useState<Company>();
	const [selectedCompanySubscriptions, setSelectedCompanySubscriptions] =
		useState<Subscription[]>([]);

	const isSingleSubscriber = originalSubscription?.subscribers?.length === 1;

	const subscriptionColumns = [
		subscriptionTableColumns.application(),
		subscriptionTableColumns.plan(),
		subscriptionTableColumns.status(),
		subscriptionTableColumns.seats(),
		subscriptionTableColumns.type(),
		subscriptionTableColumns.description(),
	];

	const { data: companyOptions, status: companyLoadingStatus } =
		useGetOnMount<Company[]>(`${ACCOUNT_API_COMPANIES}/GetCompanies`);

	const {
		data: companySubscriptions,
		getData: getCompanySubscriptions,
		status: companySubscriptionsStatus,
	} = useGet<Subscription[]>(
		`${SUBSCRIPTIONS_GET_BY_COMPANY}?companyId=${
			selectedCompany ? selectedCompany.id : 0
		}`,
	);

	useEffect(() => {
		if (selectedCompany) {
			setSelectedSubscriptionId(undefined);
			(async () => {
				await getCompanySubscriptions();
			})();
		}
	}, [selectedCompany]);

	useEffect(() => {
		if (companySubscriptions) {
			setSelectedCompanySubscriptions(companySubscriptions);
		}
	}, [companySubscriptions]);

	const [reallocateSubscription, reallocateSubscriptionStatus] = useSubmit(
		`${SUBSCRIPTIONS_API_ROUTE}/ReallocateSubscription`,
		POST,
		{
			messageOnSuccess: 'Subscription was successfully reallocated',
			messageOnError: 'Error reallocating subscription',
			showApiError: true,
		},
	);

	const onCompanySelectedFromList = (companyId: number) => {
		const company = companyOptions.find((c) => c.id === companyId);

		setSelectedCompany(company);
	};

	const eligableForReallocation = (sub: Subscription) => {
		const isActive = sub.status === SubscriptionStatus.Active;
		const isNotOriginal = sub.id !== originalSubscription?.id;
		const hasEnoughAvailableSeats =
			sub.seats - sub.occupiedSeats >=
			originalSubscription!.occupiedSeats;

		return isActive && isNotOriginal && hasEnoughAvailableSeats;
	};

	const resetForm = () => {
		setSelectedCompany(undefined);
		setSelectedCompanySubscriptions([]);
		setSelectedSubscriptionId(undefined);
	};

	const closeModal = () => {
		resetForm();
		onClose();
	};

	return (
		<StandardModal
			isOpen={isOpen}
			onClose={closeModal}
			minHeightPx={460}
			header={`Reallocate Subscription #${originalSubscription?.id}`}
			footer={
				<HStack gap="md">
					<Button
						text="Cancel"
						size="sm"
						variant="secondary"
						onClick={closeModal}
					/>
					<Button
						text="Reallocate"
						size="sm"
						type="submit"
						isLoading={
							reallocateSubscriptionStatus ===
							RequestStatus.LOADING
						}
						onClick={async () => {
							const result = await reallocateSubscription({
								fromSubscriptionId: originalSubscription?.id,
								toSubscriptionId: selectedSubscriptionId,
							});

							if (result?.success) {
								onSuccess(selectedSubscriptionId!);
							}
						}}
						isDisabled={!selectedSubscriptionId}
					/>
				</HStack>
			}
		>
			<VStack gap="lg">
				<Text>
					{`This will affect the following ${
						isSingleSubscriber ? 'subscriber' : 'subscribers'
					}:`}{' '}
				</Text>
				{!!originalSubscription?.subscribers &&
					originalSubscription.subscribers.map((user) => (
						<Text key={user.email} color="textWarning">
							{user.email}
						</Text>
					))}
				<CompanySelect
					companies={companyOptions}
					value={selectedCompany ? selectedCompany.id : null}
					onChange={onCompanySelectedFromList}
					label="Select a company to list their eligible subscriptions"
					required
					loading={companyLoadingStatus === RequestStatus.LOADING}
				/>
				<Text>
					<Text as={'span'} color="textWarning">
						Note:{' '}
					</Text>{' '}
					{isSingleSubscriber
						? 'Only active subscriptions are shown, and only those with an available seat can be selected.'
						: 'Only active subscriptions are shown, and only those with a sufficient number of available seats can be selected.'}
				</Text>
				{companySubscriptionsStatus !== RequestStatus.UNLOADED && (
					<SubscriptionTable
						heading=""
						expandable={false}
						showRowCount={false}
						onRowClicked={(subscription) =>
							setSelectedSubscriptionId(subscription.id)
						}
						columns={subscriptionColumns}
						subscriptions={selectedCompanySubscriptions?.filter(
							(sub) => sub.status === SubscriptionStatus.Active,
						)}
						requestStatus={companySubscriptionsStatus}
						selectable={(row: Row<Subscription>) =>
							eligableForReallocation(row.original)
						}
					/>
				)}
			</VStack>
		</StandardModal>
	);
};
